import { CARD_H, CARD_W } from "@webscan/constants.js";
import { printingsOf } from "@webscan/catalogue.js";
import {
  applySetSymbol,
  decide,
  DECISION,
  MAX_CANDIDATES,
  FEATURE_REGION,
  sharesArtwork,
  verifyShortlist,
  type Candidate,
} from "@webscan/decide.js";
import { ocrWasLegible, parseCollectorBlock } from "@webscan/text.js";
import { orbVerify } from "@webscan/cvclient.js";
import { preprocessForModel } from "@webscan/embed.js";
import { distanceToScore, embedDistances, gallery, retrieve, type Hit } from "@webscan/retrieve.js";
import { signatureOf } from "@webscan/signature.js";
import { inspectSleeve, trimRing } from "@webscan/sleeve.js";
import { rotate180 } from "@webscan/warp.js";
import type { Loaded } from "./assets";
import type { Identification } from "../protocol/events";
import { clock, group, groupEnd, log, ms, table } from "../shared/debug";
import { artBoxGray, referenceFeatures } from "./orb";
import { settings } from "../shared/settings";
import { applyCollectorEvidence, refineWithText } from "./collector-evidence";
import {
  COLLECTOR_MIN_HEIGHT,
  type CollectorEvidence,
  type CollectorStrips,
} from "./collector-sampling";

/**
 * One warped card to a verdict — the browser's `Recognizer.identify`.
 *
 * The funnel is the Python one, staged by cost: retrieve on hashes and the embedding,
 * expand every hit to its printings, verify the shortlist geometrically, decide — and
 * only when that is not already ACCEPTED, spend more. The printed set symbol runs on
 * AMBIGUOUS (it separates sets); OCR runs on anything not accepted and is the one
 * channel that separates two printings of one artwork *within* a set, by the printed
 * collector number. An unresolved reprint still surfaces as AMBIGUOUS — the README's
 * prescribed answer — rather than being guessed.
 *
 * Verification runs only where it can change the answer, which is a band with a bound on
 * each side. A frame whose score *ceiling* cannot reach the accept threshold is rejected
 * whatever the geometry says ({@link scoreCeiling}), and a leader whose *floor* already
 * clears the threshold by more than the margin over every rival's ceiling is accepted
 * whatever the geometry says ({@link alreadySettled}). Both tails skip the shortlist, its
 * range reads and its OpenCV round trip; the contested middle is what ORB is for.
 */

/** The answer's shape is part of the contract with the view — see `protocol/events.ts`. */
export type { Identification };

const EMBED_SIZE = 224;

/**
 * The best score any candidate of this frame could still reach.
 *
 * The fused score is monotone in the verification evidence, so its ceiling is known
 * from the hash score alone — before ORB has run. A frame whose ceiling sits under the
 * accept threshold ends REJECTED no matter what the expensive stages say, so they are
 * skipped (measured 250-400 ms per junk frame on a phone). Negative fitted weights
 * (verify_score is collinear with inliers and can dip below zero) are handled by
 * taking each evidence term only when it helps.
 */
function scoreCeiling(hashScore: number): number {
  const calibration = gallery.calibration;
  if (calibration) {
    const [wHash, wVerify, wInliers] = calibration.weights;
    const z = calibration.bias + wHash * hashScore + Math.max(0, wVerify) + Math.max(0, wInliers);
    return 1 / (1 + Math.exp(-Math.max(-30, Math.min(30, z))));
  }
  return (
    (DECISION.weightHash * hashScore + DECISION.weightVerify) /
    (DECISION.weightHash + DECISION.weightVerify)
  );
}

/**
 * The worst score a candidate can still end at — the same bound from below.
 *
 * The mirror of {@link scoreCeiling}, and it takes each evidence term the other way for
 * the same reason: a fitted weight may be negative (verify_score is collinear with
 * inliers), so the worst case for a term is zero when its weight is positive and the
 * full term when it is not. With today's positive weights this is simply "no geometric
 * support at all", which is what an unverified candidate already has.
 */
function scoreFloor(hashScore: number): number {
  const calibration = gallery.calibration;
  if (calibration) {
    const [wHash, wVerify, wInliers] = calibration.weights;
    const z = calibration.bias + wHash * hashScore + Math.min(0, wVerify) + Math.min(0, wInliers);
    return 1 / (1 + Math.exp(-Math.max(-30, Math.min(30, z))));
  }
  return (DECISION.weightHash * hashScore) / (DECISION.weightHash + DECISION.weightVerify);
}

/**
 * Whether the answer is already settled, whatever verification would have said.
 *
 * The expensive stage is worth running only where it can change the verdict, and the two
 * bounds say exactly where that is. A leader already clearing the accept threshold on its
 * floor, by more than the margin over the *ceiling* of the best candidate from a
 * different artwork, wins under every possible ORB outcome: the worst case for it is no
 * geometric support at all, the best case for the rival is perfect support, and even then
 * the order and the threshold hold. Measured on a clean read — hash 0.827 against a
 * runner-up at 0.212 — that is 0.615 against a rival ceiling of 0.240, decided by 0.375
 * where 0.06 was needed.
 *
 * Siblings of one artwork are deliberately not consulted: they tie on every retrieval and
 * geometry signal by construction, so ORB cannot separate them either. What separates
 * them is a printed identifier, which is the set-symbol and OCR stages further down.
 */
function alreadySettled(candidates: Candidate[]): number | null {
  const leader = candidates[0];
  const floor = scoreFloor(leader.hashScore);
  if (floor < DECISION.acceptThreshold) return null;
  // Ordered by retrieval distance, so the first rival from another artwork is the one
  // with the highest reachable score; if it cannot close the gap, none of them can.
  const rival = candidates.find((candidate) => !sharesArtwork(leader, candidate));
  if (rival && floor - scoreCeiling(rival.hashScore) < DECISION.margin) return null;
  return floor;
}

/**
 * Embed cards with the original, quantised model preprocessing. One orientation is
 * normally read at a time so a strong upright match avoids the second inference.
 */
async function embedBatch(
  loaded: Loaded,
  cards: Uint8ClampedArray[],
): Promise<Array<Float32Array | null>> {
  const session = loaded.embedder;
  const stride = 3 * EMBED_SIZE * EMBED_SIZE;
  const batch =
    cards.length === 1 ? preprocessForModel(cards[0]) : new Float32Array(cards.length * stride);
  if (cards.length !== 1)
    cards.forEach((card, i) => batch.set(preprocessForModel(card), i * stride));
  // The wrapper owns persistent input/output GPU buffers. Only this CPU preprocessing
  // array and the tiny normalised result are per-read allocations.
  const raw = await session.run(batch, cards.length);
  const dim = raw.length / cards.length;

  return cards.map((_, i) => {
    // Always normalise. An unmodified graph returns a unit vector already, so this is
    // either the normalisation or a no-op, and nothing downstream needs to know which.
    const vector = new Float32Array(dim);
    let norm = 0;
    for (let d = 0; d < dim; d++) {
      const value = raw[i * dim + d];
      vector[d] = value;
      norm += value * value;
    }
    norm = Math.sqrt(norm) || 1;
    for (let d = 0; d < dim; d++) vector[d] /= norm;
    return vector;
  });
}

/**
 * Retrieve at both orientations and keep the better one.
 *
 * A card in a frame can be the right way up or upside down and nothing upstream knows
 * which, so both are read and the one that retrieves at a lower distance wins. This is
 * `_retrieve_best_orientation`, and it has the same known weakness: on a near-symmetric
 * full-art basic land the hash can prefer the wrong one.
 */
async function retrieveBestOrientation(
  loaded: Loaded,
  card: Uint8ClampedArray,
): Promise<{ card: Uint8ClampedArray; hits: Hit[]; flipped: boolean }> {
  // Read upright first, and stop there when it is unambiguously good. Reading both costs
  // a second embed, a second signature and a second scan of every hash column — about
  // 60 ms on a phone — to answer a question that is usually already settled: measured
  // upright distances of 4.1 and 6.6 came back against 14.0 and 13.4 flipped.
  //
  // The floor is deliberately well inside the radius the shortlist uses. A card that only
  // *nearly* matches upright still pays for the flip, because that is exactly the case
  // where a near-symmetric full-art land can prefer the wrong way up.
  const upright = await readOrientation(loaded, card, false);
  // Switched off, the upright reading is the reading — the cost is exactly the cards that
  // arrive the wrong way up, which is what the setting says.
  if (!settings.flipCheck) return upright;
  if (upright.hits.length && upright.hits[0].distance <= FLIP_SKIP_DISTANCE) {
    log(
      "read",
      `upright d=${upright.hits[0].distance.toFixed(2)} <= ${FLIP_SKIP_DISTANCE} — ` +
        `skipping the 180° reading`,
    );
    return upright;
  }
  const flipped = await readOrientation(loaded, rotate180(card, CARD_W, CARD_H), true);
  const uprightBest = upright.hits.length ? upright.hits[0].distance : Infinity;
  const flippedBest = flipped.hits.length ? flipped.hits[0].distance : Infinity;
  log(
    "read",
    `upright d=${uprightBest.toFixed(2)}, flipped d=${flippedBest.toFixed(2)} → ` +
      `keeping ${flippedBest < uprightBest ? "flipped" : "upright"}`,
  );
  return flippedBest < uprightBest ? flipped : upright;
}

/**
 * Retrieval distance below which the upright reading is taken as settled.
 *
 * Well inside `RET.maxDistance` (28), which is the radius the shortlist is cut at — this
 * is "clearly right", not "good enough to shortlist".
 */
const FLIP_SKIP_DISTANCE = 7;

/** Embed, hash and retrieve one orientation. */
async function readOrientation(
  loaded: Loaded,
  card: Uint8ClampedArray,
  flipped: boolean,
): Promise<{ card: Uint8ClampedArray; hits: Hit[]; flipped: boolean }> {
  // Timed separately, because "retrieval" was three unrelated costs under one number and
  // there is no point optimising the wrong one: hashing the raster, one gemv over every
  // indexed vector, and a scan of the legacy hash columns.
  const embedded = clock();
  const [vector] = await embedBatch(loaded, [card]);
  const embedMs = embedded();

  const signed = clock();
  const signature = signatureOf(card);
  const signMs = signed();

  const gemv = clock();
  const [distance] = embedDistances([vector]);
  const gemvMs = gemv();

  const scanned = clock();
  const hits = retrieve(signature, distance);
  const scanMs = scanned();

  log(
    "read",
    `${flipped ? "flipped" : "upright"}: embed ${ms(embedMs)}, signature ${ms(signMs)}, ` +
      `embed-gemv ${ms(gemvMs)}, hash scan ${ms(scanMs)} — ` +
      `${hits.length} hits, best d=${hits[0]?.distance.toFixed(2) ?? "none"}`,
  );
  return { card, hits, flipped };
}

/**
 * Expand hits into printings, verify, and decide.
 *
 * Every hit expands, not just the ones that get verified. Narrowing the candidate list to
 * the verified shortlist would quietly narrow both of `decide`'s margin rules — the
 * "best candidate from a different artwork" search and the shared-art runner-up test —
 * and let the page accept on a margin the desktop would have denied.
 *
 * `desleeved` marks the one recursive retry: a REJECTED read whose warp carries a
 * sleeve ring is re-identified with the ring trimmed away (see `@webscan/sleeve.js`).
 * The retry can only replace a rejection with something better, never the reverse.
 */
export async function identify(
  loaded: Loaded,
  card: Uint8ClampedArray,
  desleeved = false,
  collector?: CollectorEvidence,
): Promise<Identification> {
  const total = clock();
  group("read", desleeved ? "identifying (de-sleeved retry)…" : "identifying…");
  const best = await retrieveBestOrientation(loaded, card);
  const hits = best.hits;

  table(
    hits.slice(0, 8).map((hit) => ({
      row: hit.row,
      distance: +hit.distance.toFixed(2),
      hashScore: +distanceToScore(hit.distance).toFixed(3),
      embedDistance: hit.embedDistance == null ? null : +hit.embedDistance.toFixed(3),
      name: printingsOf(hit.row)[0]?.name ?? "?",
    })),
  );

  const candidates: Candidate[] = [];
  outer: for (const hit of hits) {
    for (const printing of printingsOf(hit.row)) {
      candidates.push({
        row: hit.row,
        printing,
        distance: hit.distance,
        hashScore: distanceToScore(hit.distance),
        verifyScore: 0,
        verifyInliers: 0,
        cornerError: Infinity,
        matches: 0,
        ocrNameScore: 0,
        collectorMatch: null,
        setCodeMatch: null,
        symbolMatch: null,
        symbolScore: null,
        score: 0,
      });
      if (candidates.length >= MAX_CANDIDATES) break outer;
    }
  }
  if (!candidates.length) {
    log("verdict", `REJECTED — nothing within the retrieval radius (${ms(total())})`);
    groupEnd();
    // A ring wide enough can push even the right card out of the retrieval radius, so
    // the de-sleeve retry applies here too.
    if (!desleeved && settings.sleeveRetry) {
      const { ring } = inspectSleeve(card);
      const trimmed = ring && trimRing(card, ring);
      if (trimmed) {
        log("read", `sleeve ring suspected — retrying de-sleeved`);
        const retry = await identify(loaded, trimmed, true);
        if (retry.verdict !== "REJECTED") return retry;
      }
    }
    return {
      verdict: "REJECTED",
      reason: "no candidate within the retrieval radius",
      candidates: [],
      printing: null,
      verified: false,
      settled: false,
      flipped: best.flipped,
    };
  }
  log("read", `${hits.length} hits expanded to ${candidates.length} printings`);

  // ---- geometric verification ------------------------------------------------

  let verified = false;
  const ceiling = scoreCeiling(candidates[0].hashScore);
  const hopeless = ceiling < DECISION.acceptThreshold;
  const settled = hopeless ? null : alreadySettled(candidates);
  const decided = hopeless || settled !== null;
  if (hopeless) {
    log(
      "read",
      `verification skipped — with perfect geometry this frame tops out at ` +
        `${ceiling.toFixed(3)}, below threshold ${DECISION.acceptThreshold}`,
    );
  } else if (settled !== null) {
    const rival = candidates.find((candidate) => !sharesArtwork(candidates[0], candidate));
    log(
      "read",
      `verification skipped — retrieval alone scores ${settled.toFixed(3)} unverified, ` +
        `over threshold ${DECISION.acceptThreshold} and clear of ` +
        (rival
          ? `${printingsOf(rival.row)[0]?.name ?? rival.row}'s ceiling ` +
            `${scoreCeiling(rival.hashScore).toFixed(3)}`
          : "every other artwork") +
        ` — no ORB result could change the verdict`,
    );
  } else if (!settings.orb) {
    log("read", "ORB verification off in settings — verify_score stays 0 for every candidate");
  } else if (!loaded.orb) {
    log("read", "ORB verification unavailable — verify_score stays 0 for every candidate");
  }
  if (!decided && settings.orb && loaded.orb && loaded.spans) {
    const orbClock = clock();
    const shortlist = verifyShortlist(hits);
    try {
      // The range requests go out together — they are independent reads of one static
      // file — and the shortlist is then scored in a single worker round trip, so OpenCV
      // extracts the query's features once rather than once per rival.
      const references = await Promise.all(
        shortlist.map((hit) => referenceFeatures(loaded.spans!, hit.row).catch(() => null)),
      );
      const usable = shortlist
        .map((hit, i) => ({ hit, reference: references[i] }))
        .filter((entry): entry is { hit: Hit; reference: NonNullable<(typeof references)[0]> } =>
          Boolean(entry.reference),
        );
      if (usable.length) {
        const scored = await orbVerify(
          artBoxGray(best.card),
          FEATURE_REGION.width,
          FEATURE_REGION.height,
          usable.map((entry) => entry.reference),
        );
        const byRow = new Map(usable.map((entry, i) => [entry.hit.row, scored.verdicts[i]]));
        for (const candidate of candidates) {
          const verdict = byRow.get(candidate.row);
          if (!verdict) continue;
          candidate.verifyScore = verdict.score;
          candidate.verifyInliers = verdict.inliers;
          candidate.cornerError = verdict.cornerError;
          candidate.matches = verdict.matches;
        }
        verified = true;
        // queryCount is the discriminator when every verdict comes back at zero: no
        // features in the crop is a different failure from features that match nothing.
        log(
          "read",
          `ORB ${ms(orbClock())} — ${usable.length}/${shortlist.length} references fetched, ` +
            `${scored.queryCount} features in the query crop ` +
            `(${FEATURE_REGION.width}x${FEATURE_REGION.height} art box)`,
        );
        if (!scored.queryCount) {
          log("read", "no ORB features in the query — the crop is too soft to verify against");
        }
        table(
          usable.map((entry, i) => ({
            row: entry.hit.row,
            refFeatures: entry.reference.count,
            inliers: scored.verdicts[i].inliers,
            matches: scored.verdicts[i].matches,
            cornerError: +scored.verdicts[i].cornerError.toFixed(2),
            verifyScore: +scored.verdicts[i].score.toFixed(3),
          })),
        );
      } else {
        log("read", `ORB skipped — no references available for the top ${shortlist.length} hits`);
      }
    } catch (error) {
      // Verification is evidence, not a prerequisite — but a silent failure here is the
      // single most likely reason a card that should accept comes back ambiguous.
      log("read", `ORB failed after ${ms(orbClock())} — continuing on retrieval alone`, error);
    }
  }

  // Decide on hashes and geometry first; the printed set symbol only matters when the
  // printing is unresolved. An under-threshold frame rejects and an accepted frame
  // accepts with or without it, so its cost — an offset grid of edge patches per frame
  // preset — is paid only for the AMBIGUOUS verdicts it can actually resolve.
  // `verified` is the honest answer to "did ORB run", which is what lets `decide` read a
  // zero verify_score as "not measured" rather than "measured and refused" — the two are
  // the same number and opposite evidence. A read the bounds settled without running ORB
  // counts as unverified here too, which costs nothing: it already clears the threshold.
  let decision = decide(candidates, false, verified);
  if (decision.verdict === "AMBIGUOUS" && settings.setSymbols) {
    const symbolClock = clock();
    applySetSymbol(best.card, candidates);
    const checked = candidates.filter((c) => c.symbolMatch != null);
    // The scores matter more than the verdict when nothing matches: a top score just
    // under the floor means a near-miss, one around 0.5 means the crop saw wrong pixels.
    const strongest = checked
      .filter((c) => c.symbolScore != null)
      .sort((a, b) => b.symbolScore! - a.symbolScore!)
      .slice(0, 3)
      .map((c) => `${c.printing.setCode}=${c.symbolScore!.toFixed(3)}`);
    log(
      "read",
      `set symbol ${ms(symbolClock())} — ${checked.length}/${candidates.length} candidates had ` +
        `a reference, ${checked.filter((c) => c.symbolMatch).length} matched` +
        (strongest.length ? ` (strongest: ${strongest.join(", ")})` : ""),
    );
    decision = decide(candidates, false, verified);
  }

  // OCR is the last resort and the only channel that separates two printings of one
  // artwork *within one set* (mul 21 vs mul 151): the collector number printed bottom
  // left. It runs when the cheaper evidence has not accepted, exactly as the desktop
  // does — and an illegible read abstains rather than voting, because a blurry crop
  // scored as zero drags a correct answer below the threshold.
  const ocrLegibleSize = !!collector && collector.height >= COLLECTOR_MIN_HEIGHT;
  const needsOcr = decision.verdict !== "ACCEPTED" && settings.ocr && loaded.ocr && ocrLegibleSize;
  const strips = needsOcr
    ? collector?.sample
      ? collector.sample(best.flipped)
      : best.flipped
        ? collector?.flipped
        : collector?.upright
    : null;
  if (decision.verdict !== "ACCEPTED" && settings.ocr && loaded.ocr && ocrLegibleSize && strips) {
    const ocrClock = clock();
    const lead = decision.candidates[0].printing;
    const text = await loaded.ocr.read(best.card, lead.frame || "", !!lead.fullArt, strips);
    const info = parseCollectorBlock(text.collector.text, text.setInfo.text);
    const legible = ocrWasLegible(text, info);
    log(
      "read",
      `ocr ${ms(ocrClock())} — title "${text.title.text}", collector "${text.collector.text}", ` +
        `set "${text.setInfo.text}"` +
        (legible
          ? ` → number ${info.collectorNumber || "—"}, set ${info.setCode || "—"}`
          : " — illegible, abstaining"),
    );
    if (legible) {
      applyCollectorEvidence(candidates, text);
      decision = decide(candidates, true, verified);
    }
  } else if (decision.verdict !== "ACCEPTED" && settings.ocr && loaded.ocr) {
    log(
      "read",
      `OCR skipped — ${collector?.height.toFixed(0) ?? "unknown"} sensor px of card; ` +
        `needs ${COLLECTOR_MIN_HEIGHT}px and a sensor strip`,
    );
  }

  table(
    decision.candidates.slice(0, 6).map((c) => ({
      name: c.printing.name,
      set: `${c.printing.setCode} ${c.printing.collector}`,
      score: +c.score.toFixed(3),
      hash: +c.hashScore.toFixed(3),
      verify: +c.verifyScore.toFixed(3),
      inliers: c.verifyInliers,
      symbol: c.symbolMatch,
      symbolScore: c.symbolScore == null ? null : +c.symbolScore.toFixed(3),
      frame: c.printing.frame,
    })),
  );
  log(
    "verdict",
    `${decision.verdict}${decision.reason ? ` — ${decision.reason}` : ""} ` +
      `[total ${ms(total())}, threshold ${DECISION.acceptThreshold}, margin ${DECISION.margin}, ` +
      `${gallery.calibration ? "calibrated" : "hand-weighted"}]`,
  );
  groupEnd();

  // A rejected warp that carries a sleeve ring gets one de-sleeved retry. The card's
  // edge can have less contrast against the sleeve's backing than the sleeve has
  // against the table — a pale card on a white back, a black border on a black back —
  // and the detector then quads the sleeve, not the card. Trimming the ring and
  // re-reading rescues exactly that; a retry that also rejects changes nothing.
  if (decision.verdict === "REJECTED" && !desleeved && settings.sleeveRetry) {
    const { ring, bands, centre } = inspectSleeve(card);
    const trimmed = ring && trimRing(card, ring);
    if (trimmed) {
      log(
        "read",
        `sleeve ring suspected (left ${ring.left}, top ${ring.top}, right ${ring.right}, ` +
          `bottom ${ring.bottom} px) — retrying de-sleeved`,
      );
      const retry = await identify(loaded, trimmed, true);
      if (retry.verdict !== "REJECTED") return retry;
    } else {
      // Said out loud, because "the retry never fired" is indistinguishable from "the
      // detector found nothing" without the numbers it measured.
      log(
        "read",
        `no sleeve ring to trim (bands l${bands.left} t${bands.top} r${bands.right} ` +
          `b${bands.bottom}, centre stddev ${centre.toFixed(0)})`,
      );
    }
  }

  return {
    verdict: decision.verdict,
    reason: decision.reason,
    candidates: decision.candidates,
    printing: decision.candidates[0]?.printing ?? null,
    verified,
    settled: settled !== null,
    flipped: best.flipped,
  };
}

/** One collector-only refinement; no new embedding, retrieval or ORB work. */
export async function refineCollector(
  loaded: Loaded,
  identification: Identification,
  strips: CollectorStrips,
): Promise<Identification | null> {
  if (!settings.ocr || !loaded.ocr) return null;
  return refineWithText(identification, await loaded.ocr.readCollector(strips));
}
